import ContentListPage from "../Commaon/ContentListPage";
import RedirectPicker, { LinkValue } from "../Commaon/RedirectPicker";
import { isValidPhone, serializeLink, toPhoneDigits, validateLink } from "../../utils/contentLinks";

const PLACEMENTS = [
  { value: "home_banner_1", label: "Home Banner 1 (Flash Deal)" },
  { value: "home_banner_2", label: "Home Banner 2 (Promo)" },
  { value: "home_steps", label: "Home Steps" },
  { value: "search_test", label: "Search Test Page" },
  { value: "reports", label: "Reports Page" },
];

// Home placements redirect on tap; the Search Test / Reports banners carry Call + WhatsApp buttons.
const LINKABLE_PLACEMENTS = ["home_banner_1", "home_banner_2", "home_steps"];
const CONTACT_PLACEMENTS = ["search_test", "reports"];

const getPlacementLabel = (placement) =>
  PLACEMENTS.find((item) => item.value === placement)?.label || placement || "-";

const HINT =
  "The app shows the first active banner of each placement (lowest position) and falls back to its built-in image when none is active.";

const getButtonsLabel = (buttons) =>
  buttons ? `Call: ${buttons.callNumber || "-"} / WhatsApp: ${buttons.whatsappNumber || "-"}` : "-";

const renderAction = (banner) => {
  if (CONTACT_PLACEMENTS.includes(banner.placement)) {
    const text = getButtonsLabel(banner.buttons);
    return (
      <span className="text-truncate d-inline-block align-bottom" style={{ maxWidth: "100%" }} title={text}>
        {text}
      </span>
    );
  }
  return banner.link ? <LinkValue link={banner.link} truncate /> : <span className="text-muted">-</span>;
};

const EXTRA_COLUMNS = [
  { header: "Placement", width: "190px", render: (banner) => getPlacementLabel(banner.placement) },
  { header: "Redirect / Buttons", render: renderAction },
];

const PhoneInput = ({ id, label, value, error, onChange, disabled }) => (
  <div className="mb-3">
    <label className="form-label" htmlFor={id}>
      {label}
    </label>
    <input
      id={id}
      type="tel"
      inputMode="numeric"
      maxLength={10}
      className={`form-control ${error ? "is-invalid" : ""}`}
      placeholder="10-digit mobile number"
      value={value || ""}
      onChange={(e) => onChange(toPhoneDigits(e.target.value))}
      disabled={disabled}
    />
    {error && <div className="invalid-feedback">{error}</div>}
  </div>
);

const BannersList = () => (
  <ContentListPage
    title="Banners"
    singular="banner"
    plural="banners"
    endpoint="/admin/banner"
    imageField="bannerImage"
    hint={HINT}
    extraColumns={EXTRA_COLUMNS}
    getEmptyForm={() => ({
      placement: PLACEMENTS[0].value,
      link: null,
      callNumber: "",
      whatsappNumber: "",
    })}
    toForm={(banner) => ({
      placement: banner.placement || PLACEMENTS[0].value,
      link: banner.link || null,
      callNumber: banner.buttons?.callNumber || "",
      whatsappNumber: banner.buttons?.whatsappNumber || "",
    })}
    validate={(form) => {
      const errors = { placement: form.placement ? "" : "Select a placement" };
      if (LINKABLE_PLACEMENTS.includes(form.placement)) errors.link = validateLink(form.link);
      if (CONTACT_PLACEMENTS.includes(form.placement)) {
        errors.callNumber = isValidPhone(form.callNumber) ? "" : "Enter a valid 10-digit Call number";
        errors.whatsappNumber = isValidPhone(form.whatsappNumber) ? "" : "Enter a valid 10-digit WhatsApp number";
      }
      return errors;
    }}
    appendFormData={(formData, form) => {
      formData.append("placement", form.placement);
      formData.append("link", LINKABLE_PLACEMENTS.includes(form.placement) ? serializeLink(form.link) : "");
      if (CONTACT_PLACEMENTS.includes(form.placement)) {
        formData.append(
          "buttons",
          JSON.stringify({ callNumber: form.callNumber, whatsappNumber: form.whatsappNumber })
        );
      }
    }}
    renderLeadingFields={({ form, setField, errors, loading }) => (
      <div className="mb-3">
        <label className="form-label">Placement</label>
        <select
          className={`form-select ${errors.placement ? "is-invalid" : ""}`}
          value={form.placement}
          onChange={(e) => setField("placement", e.target.value)}
          disabled={loading}
        >
          {PLACEMENTS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        {errors.placement && <div className="invalid-feedback">{errors.placement}</div>}
      </div>
    )}
    renderTrailingFields={({ form, setField, errors, loading }) => {
      if (LINKABLE_PLACEMENTS.includes(form.placement)) {
        return (
          <RedirectPicker
            value={form.link}
            onChange={(link) => setField("link", link)}
            error={errors.link}
            disabled={loading}
          />
        );
      }
      if (CONTACT_PLACEMENTS.includes(form.placement)) {
        return (
          <>
            <PhoneInput
              id="banner-call-number"
              label="Call Button Number"
              value={form.callNumber}
              error={errors.callNumber}
              onChange={(value) => setField("callNumber", value)}
              disabled={loading}
            />
            <PhoneInput
              id="banner-whatsapp-number"
              label="WhatsApp Button Number"
              value={form.whatsappNumber}
              error={errors.whatsappNumber}
              onChange={(value) => setField("whatsappNumber", value)}
              disabled={loading}
            />
          </>
        );
      }
      return null;
    }}
    getViewDetails={(banner) => [
      { label: "Placement", value: getPlacementLabel(banner.placement) },
      CONTACT_PLACEMENTS.includes(banner.placement)
        ? { label: "Buttons", value: getButtonsLabel(banner.buttons) }
        : { label: "Redirect", value: <LinkValue link={banner.link} /> },
    ]}
  />
);

export default BannersList;
